import { tr } from '@repo/locale';
import { TestId } from '@repo/test-id';
import { toast } from '@repo/toast';
import { useInvalidateReservations, useInvalidateWallet } from '@repo/domain-service';
import { useEffect, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';

import {
  PAYMENT_SUCCESS_CONTENT,
  PAYMENT_SUCCESS_RETURN_BUTTON_TRANSLATION_KEY,
  PAYMENT_SUCCESS_RETURN_TOAST_TRANSLATION_KEY,
} from '@features/reservation/constants/paymentResult';
import { readPaymentResult } from '@features/reservation/utils/readPaymentResult';

import PageFrame from '@shared/components/page/PageFrame';
import { useParkingSearchStore } from '@shared/stores/useParkingSearchStore';

import { ROUTE_PATHS } from '@router/paths';

import successIllustration from '@assets/images/reservation/success-illustration.webp';

const REDIRECT_AFTER_SECONDS = 5;
const REDIRECT_AFTER_MILLISECONDS = REDIRECT_AFTER_SECONDS * 1000;

export default function SuccessPage() {
  const testId = TestId.root('reservation.success');
  const navigate = useNavigate();
  const location = useLocation();
  const invalidateReservations = useInvalidateReservations();
  const invalidateWallet = useInvalidateWallet();
  const [secondsLeft, setSecondsLeft] = useState<number>(REDIRECT_AFTER_SECONDS);

  const { product, returnTo } = readPaymentResult(location.state);
  const content = PAYMENT_SUCCESS_CONTENT[product];
  const redirectButtonTranslationKey = returnTo
    ? PAYMENT_SUCCESS_RETURN_BUTTON_TRANSLATION_KEY
    : content.redirectButtonTranslationKey;
  const redirectToastTranslationKey = returnTo
    ? PAYMENT_SUCCESS_RETURN_TOAST_TRANSLATION_KEY
    : content.redirectToastTranslationKey;

  const goFurther = (): void => {
    if (returnTo) {
      navigate(returnTo.pathname, { state: returnTo.state, replace: true });
      return;
    }
    navigate(ROUTE_PATHS[content.routeName]);
  };

  useEffect(() => {
    invalidateReservations();
    invalidateWallet();
  }, [invalidateReservations, invalidateWallet]);

  useEffect(() => {
    toast.success(tr(redirectToastTranslationKey), testId.append('redirect-toast'), {
      duration: REDIRECT_AFTER_MILLISECONDS,
    });
    // Announce the redirect once, on arrival.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    // Only a new reservation ends the parking search; an extension or a top-up leaves it as it was.
    if (product !== 'parkingSpotRental') return;
    useParkingSearchStore.getState().resetAfterReservation();
  }, [product]);

  useEffect(() => {
    if (secondsLeft <= 0) {
      goFurther();
      return;
    }

    const timeout = setTimeout(() => setSecondsLeft((prev) => prev - 1), 1000);
    return () => clearTimeout(timeout);
    // goFurther is recreated each render; the countdown alone drives the redirect.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [secondsLeft]);

  const handleRedirect = (): void => {
    goFurther();
  };

  return (
    <PageFrame
      fullScreen
      testId={testId}
      forwardButton={{
        label: tr(redirectButtonTranslationKey, { seconds: secondsLeft }),
        onClick: handleRedirect,
      }}
    >
      <div className="flex h-full w-full items-center justify-center p-4 text-center">
        <div className="inline-flex w-80 flex-col items-center justify-start gap-3">
          <img src={successIllustration} alt={tr(content.imageAltTranslationKey)} />
          <div
            className="text-heading justify-start text-2xl leading-8 font-semibold"
            data-testid={testId.append('title')}
          >
            {tr(content.titleTranslationKey)}
          </div>
          <div className="text-body justify-start text-base leading-6 font-normal">
            {tr(content.messageTranslationKey)}
          </div>
        </div>
      </div>
    </PageFrame>
  );
}
